import { useRef, useState, useEffect, useCallback } from "react";
import { X, AlertCircle } from "lucide-react";
import api from "@/lib/api";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isValidEmailAddr(s) {
  return EMAIL_RE.test((s || "").trim());
}

/**
 * Chip-based multi-recipient email input.
 *
 * Behaviour:
 *   - Type an address + Enter / comma / semicolon / Tab → adds chip
 *   - Blur with non-empty value → adds chip
 *   - Backspace on empty → removes last chip
 *   - Invalid addresses get a red ring + inline error message
 *   - Pasting a comma/semicolon-separated list adds them as chips
 *   - Typing 2+ characters fetches contact suggestions; ↑/↓ to highlight,
 *     Enter to insert. Selecting a suggestion routes through the same
 *     addRaw() path so the chip array stays a plain string[] — no shape
 *     change, no regression to existing Compose flows.
 *
 * Props:
 *   value      : string[]  current recipients
 *   onChange   : (string[]) => void
 *   placeholder: string
 *   testId     : data-testid prefix (e.g. "compose-to") — chips become
 *                `${testId}-chip-${index}`, input becomes `${testId}-input`
 *   autoFocus  : boolean
 */
export default function RecipientChipsInput({
  value, onChange, placeholder, testId, autoFocus,
}) {
  const [draft, setDraft] = useState("");
  const [showInvalid, setShowInvalid] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [openSuggest, setOpenSuggest] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const debounceRef = useRef(null);
  const recipients = Array.isArray(value) ? value : [];

  const addRaw = (raw) => {
    const parts = String(raw)
      .replace(/;/g, ",")
      .split(",")
      .map((p) => p.trim())
      .filter(Boolean);
    if (!parts.length) return;
    const next = [...recipients];
    let invalid = false;
    for (const p of parts) {
      if (!isValidEmailAddr(p)) {
        invalid = true;
        continue;
      }
      // dedupe (case-insensitive)
      const lower = p.toLowerCase();
      if (next.some((x) => x.toLowerCase() === lower)) continue;
      next.push(p);
    }
    onChange(next);
    setShowInvalid(invalid);
    setDraft("");
    setSuggestions([]);
    setOpenSuggest(false);
    setActiveIdx(-1);
  };

  // Debounced contact autocomplete. Only fires when the user has typed
  // 2+ characters. Falls back silently on errors so manual entry is never
  // blocked.
  const fetchSuggestions = useCallback(async (q) => {
    try {
      const { data } = await api.get(`/contacts/autocomplete`, {
        params: { q, limit: 6 },
      });
      const lower = recipients.map((r) => r.toLowerCase());
      const filtered = (data?.results || []).filter(
        (r) => r?.email && !lower.includes(r.email.toLowerCase())
      );
      setSuggestions(filtered);
      setOpenSuggest(filtered.length > 0);
      setActiveIdx(filtered.length > 0 ? 0 : -1);
    } catch {
      setSuggestions([]);
      setOpenSuggest(false);
    }
  }, [recipients]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = draft.trim();
    if (q.length < 2) {
      setSuggestions([]);
      setOpenSuggest(false);
      return;
    }
    debounceRef.current = setTimeout(() => fetchSuggestions(q), 180);
    return () => clearTimeout(debounceRef.current);
  }, [draft, fetchSuggestions]);

  // Close suggestions on outside click
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpenSuggest(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const acceptSuggestion = (s) => {
    if (!s?.email) return;
    addRaw(s.email);
    if (s.contact_id) {
      api.post("/contacts/autocomplete/use", { contact_id: s.contact_id }).catch(() => {});
    }
    inputRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (openSuggest && suggestions.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIdx((i) => Math.min(i + 1, suggestions.length - 1));
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIdx((i) => Math.max(i - 1, 0));
        return;
      }
      if (e.key === "Enter" && activeIdx >= 0) {
        e.preventDefault();
        acceptSuggestion(suggestions[activeIdx]);
        return;
      }
      if (e.key === "Escape") {
        setOpenSuggest(false);
        return;
      }
    }
    if (e.key === "Enter" || e.key === "," || e.key === ";" || e.key === "Tab") {
      if (draft.trim()) {
        e.preventDefault();
        addRaw(draft);
      }
    } else if (e.key === "Backspace" && !draft && recipients.length > 0) {
      e.preventDefault();
      onChange(recipients.slice(0, -1));
    } else {
      setShowInvalid(false);
    }
  };

  const handleBlur = () => {
    if (draft.trim()) addRaw(draft);
  };

  const handlePaste = (e) => {
    const text = e.clipboardData?.getData("text") || "";
    if (text.includes(",") || text.includes(";") || text.includes("\n")) {
      e.preventDefault();
      addRaw(text.replace(/\n/g, ","));
    }
  };

  const removeAt = (idx) => {
    const next = recipients.filter((_, i) => i !== idx);
    onChange(next);
    inputRef.current?.focus();
  };

  return (
    <div ref={containerRef} className="relative">
      <div
        className="min-h-9 px-2 py-1 flex flex-wrap items-center gap-1.5 rounded-md border border-input bg-transparent text-sm focus-within:outline-none focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2"
        onClick={() => inputRef.current?.focus()}
        data-testid={`${testId}-chips`}
      >
        {recipients.map((addr, i) => {
          const ok = isValidEmailAddr(addr);
          return (
            <span
              key={`${addr}-${i}`}
              className={`inline-flex items-center gap-1 h-7 px-2 rounded-full text-xs font-medium ${
                ok
                  ? "bg-[#F0F0F0] text-[#333333]"
                  : "bg-red-50 text-red-700 border border-red-200"
              }`}
              data-testid={`${testId}-chip-${i}`}
            >
              <span className="font-mono truncate max-w-[180px]">{addr}</span>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); removeAt(i); }}
                className="text-zinc-500 hover:text-zinc-800"
                aria-label={`Remove ${addr}`}
                data-testid={`${testId}-chip-remove-${i}`}
              >
                <X size={12} />
              </button>
            </span>
          );
        })}
        <input
          ref={inputRef}
          type="email"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          onPaste={handlePaste}
          placeholder={recipients.length ? "" : (placeholder || "recipient@example.com")}
          autoFocus={autoFocus}
          className="flex-1 min-w-[140px] h-7 outline-none bg-transparent text-sm"
          data-testid={`${testId}-input`}
          autoComplete="off"
        />
      </div>
      {openSuggest && suggestions.length > 0 && (
        <div
          className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-[#E5E5E5] rounded-md shadow-lg max-h-56 overflow-y-auto"
          data-testid={`${testId}-suggest-dropdown`}
        >
          {suggestions.map((s, idx) => (
            <button
              key={s.contact_id || s.email}
              type="button"
              // Use mousedown so it fires before the input's blur handler
              // (which would otherwise commit the draft as a plain chip).
              onMouseDown={(e) => { e.preventDefault(); acceptSuggestion(s); }}
              onMouseEnter={() => setActiveIdx(idx)}
              className={`w-full px-3 py-2 flex items-center gap-2 text-left transition-colors ${
                idx === activeIdx ? "bg-[#F5F5F5]" : "hover:bg-[#F5F5F5]"
              }`}
              data-testid={`${testId}-suggest-${s.contact_id || s.email}`}
            >
              <div className="w-6 h-6 rounded-full bg-[#F0F0F0] flex items-center justify-center text-[10px] font-medium text-[#555555] shrink-0">
                {(s.name || s.email).charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                {s.name && (
                  <p className="text-xs text-[#111111] truncate">{s.name}</p>
                )}
                <p className="text-[11px] text-[#888888] font-mono truncate">{s.email}</p>
              </div>
            </button>
          ))}
        </div>
      )}
      {showInvalid && (
        <p className="text-[11px] text-red-500 mt-1 flex items-center gap-1" data-testid={`${testId}-error`}>
          <AlertCircle size={11} /> One or more addresses are invalid and were skipped.
        </p>
      )}
    </div>
  );
}
